import {
  TbBuilding, TbShieldHalf, TbPalette, TbLink, TbPuzzle, TbClipboardCheck, TbCalendarClock, TbLayoutGrid, TbLayoutDashboard,
  TbTargetArrow, TbHome,
} from "react-icons/tb";

// The rail's menus.
//
// The rail selects a menu; the context nav shows THAT menu's items and swaps when the
// rail changes. An earlier version rendered every group at once, which is not what the
// kit does and left the scanner's three tabs with nowhere to live.
//
// Settings is deliberately NOT here. The rail is for the places someone works all day,
// and configuration is not one of them; it lives on the Administration screen, reached
// from the identity at the foot of the rail. Every settings key is in OFF_NAV below so
// breadcrumbs still name them.
//
// `admin` hides an item from non-admins. `pro` (needs a Pro licence) and `gate` (needs a
// plan module switched on in Global Admin → Plans & Features — keys in lib/plan.js GATES,
// with `gateFeature` naming a switch inside it — e.g. Lead Generation's Prospecting)
// LOCK an item rather than hide it: it stays in the nav with a lock, and opening it shows
// what it does and how to upgrade. A free account that never sees a feature has no
// reason to pay for it.
//
// `flyout` is what the rail's hover panel (NavFlyout.jsx) shows besides the links:
//  - quick: "New …" actions. Each either opens a screen with a one-shot intent the
//    screen acts on (see intent.js), or names a `builder` action the shell performs.
//  - context: a column of live data for the area — the accounts, the reviews coming up —
//    so the panel holds everything needed for that area, not just a list of pages.
// A menu with no links beyond itself and no flyout (the dashboard) opens no panel.
export const MENUS = [
  // Home. First in the rail and where sign-in lands. `bare` because it is a single
  // screen that spans every area: a context nav listing just "Dashboard" beside it would
  // be a column of nothing, so the shell hides it and gives the page the full width.
  {
    key: "dashboard",
    label: "Dashboard",
    icon: TbHome,
    bare: true,
    items: [
      { key: "dashboard", label: "Dashboard", icon: TbHome },
    ],
  },
  // Lead generation: the four ways a scan reaches someone who is not a customer yet.
  //
  // Named for the job rather than the tool. "External Risk Scanner" described the engine,
  // which meant the rail was organised around how the product is built — the lead magnet,
  // prospecting and scan links all run the same scanner, and grouping them by that fact
  // told nobody when to use which.
  //
  // Lead Magnets leads because it is the only one that works while nobody is at a desk.
  {
    key: "leadgen",
    label: "Lead Generation",
    icon: TbTargetArrow,
    gate: "LEAD_GENERATION",
    flyout: {
      quick: [
        { label: "New lead magnet", screen: "lead-magnet", intent: { type: "new" } },
        { label: "New scan link", screen: "scan-links" },
      ],
      context: "leadMagnets",
    },
    items: [
      { key: "lead-magnet", label: "Lead magnets", icon: TbPuzzle, gate: "LEAD_GENERATION", gateFeature: "LEAD_MAGNETS" },
      // The widgets already built. A tab inside the builder meant the list of what exists
      // was reachable only by first opening the thing that creates more of them.
      { key: "widgets", label: "Saved widgets", icon: TbLayoutGrid, gate: "LEAD_GENERATION", gateFeature: "LEAD_MAGNETS" },
      // Prospecting is its own screen rather than a switch on the scan form. The two
      // share a module picker and nothing else: prospecting files nothing against a
      // customer, posts to a different endpoint, and ends in a report you send.
      { key: "prospecting", label: "Prospecting", icon: TbTargetArrow, gate: "LEAD_GENERATION", gateFeature: "PROSPECTING" },
      { key: "scan",        label: "External risk scan", icon: TbShieldHalf, gate: "LEAD_GENERATION", gateFeature: "EXTERNAL_RISK_SCAN" },
      // Kept under its own name. It is not a lead magnet: a scan link is minted for one
      // named prospect and emailed to them, where the lead magnet is a widget on a page
      // that captures people nobody has spoken to. Renaming this to "Lead Magnets" would
      // give two screens one name and leave the actual lead magnet builder without it.
      { key: "scan-links",  label: "Generate scan link", icon: TbLink, gate: "LEAD_GENERATION", gateFeature: "SCAN_LINKS" },
    ],
  },
  // Accounts — the old Organizations. A single screen, so it is a rail item rather than a
  // menu of one: the screen already carries its own All / Client / Prospect views, and
  // wrapping it in a group would add a sub-nav listing nothing but itself.
  {
    key: "discovery",
    label: "Discovery",
    icon: TbBuilding,
    bare: true,
    flyout: {
      quick: [{ label: "New Organization", screen: "organizations", intent: { type: "new" } }],
      context: "Discovery",
    },
    items: [
      { key: "organizations", label: "Organizations", icon: TbBuilding, countKey: "organizations" },
    ],
  },
  // Business reviews. Kept as its own area under its existing name: the five screens in
  // it are built and in use, and folding them into a deferred "Sales & QBRs" group would
  // have left every one of them reachable by URL only.
  //
  // The only menu with section labels, because "Latest scans" is client data rather than
  // a review artefact and reads oddly in the same list as the four review screens.
  {
    key: "reviews",
    label: "Business Reviews",
    icon: TbClipboardCheck,
    // One switch for the whole area (Global Admin → Scan Modules → Business Reviews).
    gate: "BUSINESS_REVIEWS",
    flyout: {
      quick: [{ label: "New assessment", screen: "assessments", intent: { type: "new" } }],
      context: "reviews",
    },
    sections: [
      {
        label: "Business reviews",
        items: [
          { key: "review-overview",   label: "Overview", icon: TbLayoutDashboard, gate: "BUSINESS_REVIEWS" },
          { key: "assessments",       label: "Assessments", icon: TbClipboardCheck, countKey: "assessments", gate: "BUSINESS_REVIEWS" },
          { key: "review-schedule",   label: "Review schedule", icon: TbCalendarClock, gate: "BUSINESS_REVIEWS" },
          { key: "review-templates",  label: "Assessment templates", icon: TbLayoutGrid, gate: "BUSINESS_REVIEWS" },
        ],
      },
      {
        label: "Client",
        items: [
          // Organizations is deliberately NOT repeated here. It used to be a second
          // screen over the same rows, and it is now one list under Accounts carrying
          // both the scan history and the review record.
          { key: "reviews-scans", label: "Latest scans", icon: TbShieldHalf, gate: "BUSINESS_REVIEWS" },
        ],
      },
    ],
  },
  // Reports. One item today; "Generated reports" belongs here when that screen exists,
  // which is why this is a menu rather than a bare rail item like Accounts.
  {
    key: "reports",
    label: "Reports",
    icon: TbPalette,
    pro: true,
    flyout: {
      quick: [{ label: "New report template", builder: "new-template" }],
      context: "templates",
    },
    items: [
      { key: "templates", label: "Report templates", icon: TbPalette, pro: true },
    ],
  },
];

// A menu may declare `sections` (labelled groups) instead of a flat `items`. Everything
// downstream — the rail, ALL_ITEMS, menuForScreen, the pro/admin filter — works off
// `items`, so it is derived once here rather than special-cased in each of them.
MENUS.forEach((menu) => {
  if (!menu.items && menu.sections) {
    menu.items = menu.sections.flatMap((section) => section.items);
  }
});

// Reachable without a nav entry, so the breadcrumb can still name them.
//
// This is load-bearing, not decoration: labelForScreen falls back to the literal string
// "Accounts" for anything it cannot find, so a screen missing from here shows the wrong
// breadcrumb rather than an obvious error.
export const OFF_NAV = {
  notifications: "Notifications",
  // The assessment editor is reached by opening an assessment, not from the nav.
  "assessment-editor": "Assessment",
  // Two-factor lives inside Profile now; the route stays reachable so old links resolve.
  "two-factor": "Two-factor",

  // Everything under Administration. Reached from the account panel, not the rail.
  administration: "Administration",
  // Uploading happens in a dialog inside the organization now; the screen stays
  // reachable so any saved link still resolves.
  upload: "Upload scan data",
  profile: "Profile",
  requests: "Feature requests",
  downloads: "Download the Scanner",
  "admin-users": "Admin panel",
  branding: "Branding",
  thresholds: "Risk thresholds",
  integrations: "Integrations",
};

// The screens reached from the account panel at the foot of the rail — Personal settings
// and Tenant admin — plus the Administration hub that lists them.
//
// These are a separate idea from OFF_NAV. OFF_NAV means "no nav entry, so the breadcrumb
// has to name it", and it also covers screens that genuinely still belong to a rail area:
// the assessment editor is opened from Business reviews and the rail should stay lit on
// it. This set is the narrower question of "did the user leave the rail entirely?", which
// is what decides whether a rail icon should still look selected.
export const ACCOUNT_SCREENS = new Set([
  // Personal settings
  "profile",
  "two-factor",
  "requests",
  // Downloads is deliberately absent: it has its own rail item above Sign out now, so
  // it is no longer somewhere you reach by leaving the rail. Listing it here would light
  // the account avatar at the same time as its own rail icon.
  // Tenant admin, and the hub that lists it
  "administration",
  "admin-users",
  "branding",
  "thresholds",
  "integrations",
]);

export const isAccountScreen = (screen) => ACCOUNT_SCREENS.has(screen);

// A screen that belongs to no rail menu. The rail keeps whatever was selected rather
// than snapping to the first menu, because these are reached from the account panel and
// have no rail area of their own to highlight.
export const isOffNav = (screen) => Object.prototype.hasOwnProperty.call(OFF_NAV, screen);

export const ALL_ITEMS = MENUS.flatMap((m) => m.items);

// The plan lock for every screen, including ones with no nav entry: the assessment
// editor is reached by opening an assessment, and must not open on a plan without
// Business Reviews just because the URL was typed. A screen inherits its menu's lock.
const OFF_NAV_LOCKS = { "assessment-editor": { gate: "BUSINESS_REVIEWS" } };
export function lockForScreen(key) {
  if (OFF_NAV_LOCKS[key]) return OFF_NAV_LOCKS[key];
  for (const menu of MENUS) {
    const item = menu.items.find((i) => i.key === key);
    if (item) {
      const pro = !!(item.pro || menu.pro);
      const gate = item.gate || menu.gate || null;
      if (!pro && !gate) return null;
      return item.gateFeature ? { pro, gate, gateFeature: item.gateFeature } : { pro, gate };
    }
  }
  return null;
}

export const DEFAULT_SCREEN = "dashboard";

// Every screen's address. The URL is the source of truth for which screen is open, so a
// screen can be bookmarked, shared, refreshed and reached with the back button.
//
// Two constraints on the spelling, both load-bearing:
//  - MfaGate exempts any path that STARTS WITH one of its excluded prefixes ('/admin',
//    '/downloads', '/report', '/scan/', '/scan-links', ...). A screen path beginning with
//    one of those would silently skip the mandatory-MFA check, which is why the admin
//    panel is /settings/users and the scanner download is /download-scanner.
//  - Each path must also be registered in App.js. V2_PATHS below is what App.js maps, so
//    adding a screen here is enough.
//
// The assessment editor takes an id: /reviews/assessments/:id.
export const SCREEN_PATHS = {
  dashboard:          "/dashboard",
  organizations:      "/organizations",
  upload:             "/organizations/upload",
  notifications:      "/notifications",
  downloads:          "/download-scanner",

  scan:               "/scanner/external-risk",
  prospecting:        "/scanner/prospecting",
  "scan-links":       "/scanner/scan-links",
  "lead-magnet":      "/scanner/lead-magnet",
  widgets:            "/scanner/widgets",

  templates:          "/templates",

  "review-overview":  "/reviews/overview",
  assessments:        "/reviews/assessments",
  "review-schedule":  "/reviews/schedule",
  "review-templates": "/reviews/templates",
  "reviews-scans":    "/reviews/latest-scans",

  administration:     "/settings",
  profile:            "/settings/profile",
  "two-factor":       "/settings/two-factor",
  requests:           "/settings/feature-requests",
  "admin-users":      "/settings/users",
  branding:           "/settings/branding",
  thresholds:         "/settings/thresholds",
  integrations:       "/settings/integrations",
};

// Route patterns for App.js. Screens that take an id get a second, parameterised route.
// Screens that take an id in the path. The lead magnet builder takes a widget key, so
// "edit this widget" is an address rather than a click that only works from one screen.
const WITH_PARAM = new Set(["assessments", "lead-magnet"]);
export const V2_PATHS = Object.entries(SCREEN_PATHS).flatMap(([key, path]) =>
  WITH_PARAM.has(key) ? [path, `${path}/:param`] : [path]
);

export const pathForScreen = (key, param) => {
  const base = SCREEN_PATHS[key] || SCREEN_PATHS[DEFAULT_SCREEN];
  return param != null && param !== "" && WITH_PARAM.has(key) ? `${base}/${encodeURIComponent(param)}` : base;
};

// The screen and optional id a pathname points at. Longest match first, so
// /organizations/upload is not read as /organizations.
const BY_LENGTH = Object.entries(SCREEN_PATHS).sort((a, b) => b[1].length - a[1].length);
export const screenForPath = (pathname) => {
  const clean = (pathname || "").replace(/\/+$/, "") || "/";
  for (const [key, path] of BY_LENGTH) {
    if (clean === path) return { key, param: null };
    if (WITH_PARAM.has(key) && clean.startsWith(`${path}/`)) {
      const rest = clean.slice(path.length + 1);
      if (rest && !rest.includes("/")) return { key, param: decodeURIComponent(rest) };
    }
  }
  return null;
};

// Old ?tab= values — v1's names and earlier v2 keys — mapped onto today's screen keys.
// Links like /dashboard?tab=Profile (the MFA banner) and ?tab=scan-links (ScanLinkRoute)
// are still issued from shared code, so they are translated rather than broken.
const TAB_ALIASES = {
  "review-organizations": "organizations",
  home: "dashboard",
};
export const screenForTab = (tab) => {
  if (!tab) return null;
  if (SCREEN_PATHS[tab]) return tab;
  const lower = tab.toLowerCase();
  if (SCREEN_PATHS[lower]) return lower;
  return TAB_ALIASES[lower] || null;
};

export const menuForScreen = (screen) =>
  (MENUS.find((m) => m.items.some((i) => i.key === screen)) || MENUS[0]).key;

export const labelForScreen = (screen) =>
  ALL_ITEMS.find((i) => i.key === screen)?.label || OFF_NAV[screen] || "Discovery";

// What sits ABOVE an off-nav screen in the breadcrumb.
//
// An off-nav screen is by definition not in any menu, so there is nothing to derive a
// parent from — it has to be stated. Getting here from the wrong place is worse than a
// flat trail, so anything not listed simply has no parent and renders as a single crumb.
const PARENT = {
  // Two-factor enrolment is rendered inline by Profile; the route survives for old links.
  "two-factor": "profile",
  // Opened by picking an assessment, so the way back up is the list you picked it from.
  "assessment-editor": "assessments",
  // Uploading happens in a dialog on the organization now, but the standalone screen is
  // still routable, and it came from the client list.
  upload: "organizations",
  // Every card on the Administration hub opens a leaf at its own address, and the
  // breadcrumb is the way back up to the hub.
  profile: "administration",
  requests: "administration",
  "admin-users": "administration",
  branding: "administration",
  thresholds: "administration",
  integrations: "administration",
};

// The breadcrumb trail for a screen, root-last-excluded: the caller prepends the tenant.
//
// Every entry carries the screen key it navigates to. The final entry is where you
// already are — the caller renders it as text, not as a link.
export const crumbsFor = (screen) => {
  const self = { key: screen, label: labelForScreen(screen) };

  const parent = PARENT[screen];
  if (parent) return [...crumbsFor(parent), self];

  const menu = MENUS.find((m) => m.items.some((i) => i.key === screen));
  // The menu crumb is only a real step up when it leads somewhere else. A single-screen
  // area (Accounts, Reports) would otherwise print its own name twice.
  if (menu && menu.items.length > 1 && menu.items[0].key !== screen) {
    return [{ key: menu.items[0].key, label: menu.label }, self];
  }
  return [self];
};
